import { Grid } from "@mui/material";
import { useSnackbar } from "notistack";
import React, { useCallback, useEffect, useState } from "react";
import { CheckSquare, XSquare } from "react-feather";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import Dialog from "src/components/ui/Dialog";
import InputSelect from "src/components/ui/InputSelect";
import InputText from "src/components/ui/InputText";
import Loading from "src/components/ui/Loading";
import { Creators as CreatorsInventory } from "src/store/ducks/inventory";
import { Creators as CreatorsStock } from "src/store/ducks/stock";
import { Creators as ProdVariationsCreators } from "src/store/ducks/products_variations";
import { getErrors, getMobileOperatingSystem } from "src/utils/getUtils";

function CreateInventoryVariations({
  open,
  close,
  title = "",
  variations_id = [],
}) {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const {
    storages,
    types,
    loading: loadingStock,
  } = useSelector((state) => state.stock);
  const { loading: loadingInventory } = useSelector((state) => state.inventory);
  const [inventory, setInventory] = useState({
    storage_id: 0,
    title: "",
    type_id: 3,
  });

  const isMobile = getMobileOperatingSystem() !== 'unknown';

  useEffect(() => onOpenDialog(), [open]);

  const onOpenDialog = useCallback(() => {
    dispatch(CreatorsStock.stockLoading(true));
    CreatorsStock.getStorageList()
      .then(({data}) => {
        dispatch([
          CreatorsStock.stockSetStorage(data),
        ]);
        setInventory({
          open: true,
          title: "",
          type_id: 3,
          storage_id: "",
        });
      })
      .catch((error) => {
        enqueueSnackbar(`${getErrors(error)}`, { variant: "error" });
      })
      .finally(() => {
        dispatch(CreatorsStock.stockLoading(false));
      });
  });

  async function createInventory() {
    try {
      if (!inventory.storage_id) {
        enqueueSnackbar("Deve-se primeiro selecionar um Armazém", {
          variant: "warning",
        });
        return;
      }

      if (!inventory.title) {
        enqueueSnackbar("Deve-se primeiro digitar um nome para este inventário", {
          variant: "warning",
        });
        return;
      }

      if (variations_id.length === 0) {
        enqueueSnackbar("Não existe nenhuma variação selecionada.", {
          variant: "warning",
        });
        return;
      }

      dispatch(CreatorsInventory.inventoryLoading(true));

      let hasPending = false;
      let hasNoPositions = false;
      let createdCount = 0;

      await Promise.all(
        variations_id.map(async (variation) => {
          const data =
            await ProdVariationsCreators.getProductVariationsPositionsMapping(
              variation,
              { exibir_zerados: true }
            );

          const noPositions =
            (data
              ?.map(v => v.positions.length)
              .reduce((a, b) => a + b, 0) ?? 0) <= 0;

          const hasPendingInventory = data?.some((v) =>
            v.positions?.some(
              (position) =>
                position?.inventory?.inventoryStatus === 1 &&
                position?.inventory?.inventoryType === "P" &&
                position?.inventory?.idfAutoAdded === "N"
            )
          );

          if (noPositions) {
            hasNoPositions = true;
            return;
          }
          if (hasPendingInventory) {
            hasPending = true;
            return;
          }

          const { codigo } = await CreatorsInventory.postInventorySave({
            stock_id: inventory.storage_id,
            name: inventory.title,
            type_id: inventory.type_id,
            idf_inventory_type: "P",
          });

          await CreatorsInventory.postInventoryProductsVariations({
            inventory_id: codigo,
            ids: [variation],
            stock_id: inventory.storage_id,
          });

          createdCount++;
        })
      );

      if (hasPending) {
        enqueueSnackbar(
          variations_id.length > 1
            ? "Existem variações com inventário pendente."
            : "Existe uma variação com inventário pendente.",
          { variant: "error" }
        );
      }

      if (hasNoPositions) {
        enqueueSnackbar(
          variations_id.length > 1
            ? "Existem variações sem posições alocadas."
            : "Existe uma variação sem posições alocadas.",
          { variant: "error" }
        );
      }

      if (createdCount > 0) {
        enqueueSnackbar("Inventário criado com sucesso.", { variant: "success" });

        setInventory({
          title: "",
          type_id: 3,
          storage_id: "",
        });

        close();
      }
    } catch (error) {
      enqueueSnackbar(`${getErrors(error)}`, { variant: "error" });
    } finally {
      dispatch(CreatorsInventory.inventoryLoading(false));
    }
  }

  return (
    <>
      <Loading
        loading={loadingStock || loadingInventory}
        style={{ zIndex: 9999 }}
      />
      <Dialog
        fullWidth
        visible={open}
        title="Criar inventário"
        buttons={[
          {
            icon: !isMobile ? <XSquare /> : <></>,
            title: "Cancelar",
            disabled: loadingStock || loadingInventory,
            color: "#ffffff",
            style: {
              flex: isMobile ? 1 : undefined
            },
            otherParams: {
              color: "neutral",
              variant: "contained",
            },
            onClick: () => close(),
          },
          {
            icon: !isMobile ? <CheckSquare /> : <></>,
            title: "Criar inventário",
            disabled: loadingStock || loadingInventory,
            style: {
              flex: isMobile ? 2 : undefined
            },
            otherParams: {
              color: "success",
              variant: "contained",
            },
            onClick: () => createInventory(),
          },
        ]}
      >
        <Grid container spacing={1}>
          <Grid item sm={12} sx={{ fontSize: 12, mb: 2 }}>
            {title}
          </Grid>
          <Grid item xs={12} sm={4}>
            <InputSelect
              label="Armazém"
              value={inventory.storage_id}
              onChange={(event) =>
                setInventory((state) => ({
                  ...state,
                  storage_id: event.target.value,
                }))
              }
              values={[
                ...storages.map((o) => ({ label: o.nome, value: o.codigo })),
              ]}
            />
          </Grid>
          <Grid item xs={12} sm={8}>
            <InputText
              label="Descrição do inventário"
              value={inventory.title}
              onChange={(event) => {
                const description = event.target.value;

                if (description?.length > 40) return;

                setInventory((state) => ({ ...state, title: description }))
              }}
            />
          </Grid>
        </Grid>
      </Dialog>
    </>
  );
}

export default CreateInventoryVariations;
